import { z } from "zod";

export const ApiErrorSchema = z.object({
  error: z.string(),
  detail: z.string().optional(),
  requestId: z.string().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

// ── Sites ─────────────────────────────────────────────
// Mirrors sandbar API's dto.SiteResponse. Note: no `id` field — sites are
// keyed by `slug` everywhere. No `build_dir` either; the API doesn't expose
// or accept it on update.
export const SiteActiveDeploySchema = z.object({
  id: z.string(),
  status: z.string(),
  message: z.string().optional(),
  file_count: z.number().int().nonnegative().optional(),
  total_bytes: z.number().int().nonnegative().optional(),
  created_at: z.string().optional(),
});
export type SiteActiveDeploy = z.infer<typeof SiteActiveDeploySchema>;

export const SiteSchema = z.object({
  slug: z.string(),
  name: z.string(),
  thumbnail_url: z.string().url().optional(),
  active_deploy_id: z.string().optional(),
  active_deploy: SiteActiveDeploySchema.optional(),
  production_branch: z.string(),
  preview_expiry: z.string().optional(),
  created_at: z.string(),
  created_by: z.string().optional(),
  updated_at: z.string().optional(),
  updated_by: z.string().optional(),
});
export type Site = z.infer<typeof SiteSchema>;

export const SiteListSchema = z.object({
  data: z.array(SiteSchema),
  next_cursor: z.string().nullish(),
});
export type SiteList = z.infer<typeof SiteListSchema>;

// ── Deploys ───────────────────────────────────────────
export const DeploySchema = z.object({
  id: z.string(),
  status: z.string(),
  message: z.string().optional(),
  branch: z.string().optional(),
  commit_sha: z.string().optional(),
  file_count: z.number().int().nonnegative(),
  total_bytes: z.number().int().nonnegative(),
  created_at: z.string(),
  activated_at: z.string().optional(),
});
export type Deploy = z.infer<typeof DeploySchema>;

export const DeployListSchema = z.object({ data: z.array(DeploySchema) });
export type DeployList = z.infer<typeof DeployListSchema>;

// ── Domains ───────────────────────────────────────────
// Mirrors sandbar API's dto.DomainResponse. SSL status field is named
// `certificate_status` on the wire (not `ssl_status`). On AddDomain the
// API emits structured `dns_record_{type,name,value}` describing the
// record the user must add — `dns_instructions` is a legacy freeform
// fallback (sometimes truncated by upstream verification providers).
export const DomainSchema = z.object({
  id: z.string(),
  hostname: z.string(),
  verification_status: z.string(),
  certificate_status: z.string(),
  redirect_to: z.string().optional(),
  dns_record_type: z.string().optional(),
  dns_record_name: z.string().optional(),
  dns_record_value: z.string().optional(),
  dns_instructions: z.string().optional(),
  created_at: z.string(),
  verified_at: z.string().optional(),
});
export type Domain = z.infer<typeof DomainSchema>;

export const DomainListSchema = z.object({ items: z.array(DomainSchema) });
export type DomainList = z.infer<typeof DomainListSchema>;

// POST /sites/{slug}/domains returns dto.DomainResponse directly (flat).
export const AddDomainResponseSchema = DomainSchema;
export type AddDomainResponse = z.infer<typeof AddDomainResponseSchema>;

// ── Billing ───────────────────────────────────────────
export const SubscriptionSchema = z.object({
  tier: z.string(),
  billing_enabled: z.boolean(),
  site_limit: z.number().int().nonnegative(),
  site_count: z.number().int().nonnegative(),
  stripe_customer_id: z.string().optional(),
  subscription_id: z.string().optional(),
  subscription_status: z.string().optional(),
  current_period_end: z.number().int().optional(),
  trial_end: z.number().int().optional(),
  cancel_at_period_end: z.boolean(),
  storage_bytes_used: z.number().int().nonnegative(),
  storage_bytes_limit: z.number().int().nonnegative(),
  bandwidth_used: z.number().int().nonnegative(),
  bandwidth_limit: z.number().int().nonnegative(),
  max_file_bytes: z.number().int().nonnegative(),
});
export type Subscription = z.infer<typeof SubscriptionSchema>;

export const PlanCardSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  features: z.array(z.string()),
  price_monthly: z.string().optional(),
  price_yearly: z.string().optional(),
});
export type PlanCard = z.infer<typeof PlanCardSchema>;

export const PlansResponseSchema = z.object({
  plans: z.array(PlanCardSchema),
});
export type PlansResponse = z.infer<typeof PlansResponseSchema>;

export const CheckoutResponseSchema = z.object({ url: z.string().url() });
export type CheckoutResponse = z.infer<typeof CheckoutResponseSchema>;

export const PortalResponseSchema = z.object({ url: z.string().url() });
export type PortalResponse = z.infer<typeof PortalResponseSchema>;

// ── Workspaces ────────────────────────────────────────
export const WorkspaceSchema = z.object({
  id: z.string(),
});
export type Workspace = z.infer<typeof WorkspaceSchema>;
